export interface ProFeatureConfig {
  featureName: string
  benefitSentence: string
  bullets: string[]
}

export const PRO_FEATURE_CONFIGS: Record<string, ProFeatureConfig> = {
  ProjectLimit: {
    featureName: 'Unlimited Take-offs',
    benefitSentence: 'Upgrade to keep creating takeoffs today',
    bullets: [
      'Unlimited projects with no caps so you never get stopped mid-work',
      'Bid on every job without restrictions that hold back your growth',
      'Scale your workflow to handle large and complex projects with ease',
    ],
  },
  TrialLimit: {
    featureName: '2 Take-offs',
    benefitSentence: 'Upgrade to keep creating takeoffs today.',
    bullets: [
      'Get a hands-on feel for our features with 2 take-offs to explore',
      'Perfect for testing out the platform on a couple of projects',
      'Upgrade to Pro for unlimited take-offs and full access to all features',
    ],
  },
  uploadPDF: {
    featureName: 'Unlimited Files',
    benefitSentence: 'Unlimited take-off files. Keep every scope and version in one place.',
    bullets: [
      'Add as many drawings as needed for a single job',
      'Keep all related plans together in one organized takeoff',
      'Switch between files instantly without creating separate projects',
    ],
  },
  FolderCreate: {
    featureName: 'Smart Files & Folders',
    benefitSentence: 'Keep your plans organized. Manage drawings anywhere.',
    bullets: [
      'Unlimited Folders, Sub-folders & files within them',
      'Scope folders become estimate scope groups automatically',
      'Store old plan versions while keeping current files organized',
    ],
  },
  Assemblies: {
    featureName: 'Assemblies',
    benefitSentence: 'Build once. Reuse assemblies across every job',
    bullets: [
      'Save time by reusing material and labor sets instead of retyping',
      'Improve accuracy with pre-built assemblies that reduce errors',
      'Standardize workflows so your whole team take-off the same way',
    ],
  },
  AutoTake: {
    featureName: 'AutoTake',
    benefitSentence: 'Take off elevations in seconds. Let AI do the work.',
    bullets: [
      'Automate elevation measurements without manual tracing',
      'Free your team from repetitive measuring tasks',
      'Handle more drawings with less effort',
    ],
  },
  AutoCount: {
    featureName: 'AutoCount',
    benefitSentence: 'Skip manual counting. Let Auto Count find symbols instantly.',
    bullets: [
      'Detect doors, windows, and fixtures in seconds',
      'Eliminate errors from hand counting symbols',
      'Save hours on repetitive takeoff work',
    ],
  },
  AddPricing: {
    featureName: 'Real-time Pricing',
    benefitSentence: 'See costs update as you work. Price every job with confidence.',
    bullets: [
      'Eliminate guesswork and bid with accuracy every time',
      'Adjust scope on the fly with live cost feedback',
      'Instantly see material and labor totals as you measure',
    ],
  },
  GenerateProposal: {
    featureName: '1 Click Proposals',
    benefitSentence: 'Create professional proposals automatically from your takeoffs.',
    bullets: [
      'Instantly generate proposals from your estimate in one shot',
      'Saves hours of data transfer to other apps',
      'Professionally formatted branded proposals right from take-off',
    ],
  },
  Assistant: {
    featureName: 'AI Assistant',
    benefitSentence: 'Ask the assistant anything about your drawings and estimate.',
    bullets: [
      'Ask questions about your plans and get answers with sheet references',
      'Let the assistant run takeoffs, price items and draft scopes for you',
      'Get guided through every step from scale to finished proposal',
    ],
  },
  DrawingRevisions: {
    featureName: 'Drawing Revisions',
    benefitSentence: 'Handle addenda and revised sets without redoing your takeoff.',
    bullets: [
      'Upload revised drawings alongside the original set',
      'Carry your measurements forward so no takeoff work is lost',
      'Compare sets with overlays to spot exactly what changed',
    ],
  },
  EstimateValidation: {
    featureName: 'Estimate Validation',
    benefitSentence: 'Catch gaps before the client does. AI checks your estimate.',
    bullets: [
      'Spot missing scopes, quantities and pricing before you send',
      'Get flagged items explained with sheet references',
      'Bid with confidence knowing the numbers hold up',
    ],
  },
  ScopeGeneration: {
    featureName: 'Scope Generation',
    benefitSentence: 'Draft a full scope of work from your takeoff in one click.',
    bullets: [
      'Generate inclusions and exclusions matched to your measurements',
      'Review and apply straight into your proposal',
      'Save the back-and-forth of writing scopes by hand',
    ],
  },
  ExportCsv: {
    featureName: 'Export CSV & Mark-ups',
    benefitSentence: 'Stop retyping data into other tools. Export your takeoffs instantly',
    bullets: [
      'Share CSV data with your team or accounting in seconds',
      'Generate PDF markups to send alongside your estimates',
      'Save time and reduce errors by eliminating manual entry',
    ],
  },
}

export function getProFeatureConfig(featureKey: string): ProFeatureConfig {
  return PRO_FEATURE_CONFIGS[featureKey]
}
